import { WALL_THICKNESS } from './constants.js';

// A per-calculation broad phase over an unchanged compiled scene. Every result
// is still tested by the existing exact geometry predicates. No IDs are used:
// two floors may legitimately contain objects with the same original ID.
export const OBSTRUCTION_INDEX_LIMITS=Object.freeze({walls:20000,vertices:200000,entries:250000,buckets:65536,wallBuckets:512,queryBuckets:4096,scopeBytes:16*1024*1024,cellSize:8,raySectors:256,rayEntries:4000000});
const finite=v=>typeof v==='number'&&Number.isFinite(v)&&Math.abs(v)<=10000000;
const epsilon=1e-7;
// Numeric bucket keys: finite coordinates and thicknesses keep |cell| below 2^22,
// so (x+2^23)*2^24+(y+2^23) is an exact, collision-free safe integer. String
// keys were the dominant cost of every query on large camera-bearing floors.
const KEY_OFFSET=8388608,KEY_SPAN=16777216;
const cellKey=(x,y)=>(x+KEY_OFFSET)*KEY_SPAN+(y+KEY_OFFSET);
function bounds(w,budget){
 if(!w||!['x1','y1','x2','y2','h'].every(k=>finite(w[k])))return null;
 const bottom=w.bottom||0;if(!finite(bottom)||bottom>w.h)return null;
 let minX=Math.min(w.x1,w.x2),maxX=Math.max(w.x1,w.x2),minY=Math.min(w.y1,w.y2),maxY=Math.max(w.y1,w.y2);
 const polygon=w.roofPts||w.wallPts;
 if(polygon){
  if(!Array.isArray(polygon)||polygon.length<3||w.roofHoles!==undefined&&!Array.isArray(w.roofHoles))return null;
  minX=minY=Infinity;maxX=maxY=-Infinity;
  for(const ring of [polygon,...(w.roofHoles||[])]){
   if(!Array.isArray(ring)||ring.length<3||(budget.vertices+=ring.length)>OBSTRUCTION_INDEX_LIMITS.vertices)return null;
   for(const p of ring){if(!p||!finite(p.x)||!finite(p.y))return null;minX=Math.min(minX,p.x);minY=Math.min(minY,p.y);maxX=Math.max(maxX,p.x);maxY=Math.max(maxY,p.y);}
  }
 }else{
  const t=w.thickness??WALL_THICKNESS;if(!finite(t)||t<0)return null;
  minX-=t/2;maxX+=t/2;minY-=t/2;maxY+=t/2;
 }
 return [minX-epsilon,minY-epsilon,maxX+epsilon,maxY+epsilon,bottom-epsilon,w.h+epsilon];
}
// Does segment a→a+d pass within margin of box o in plan? A slab test; an exact
// hit lies on the segment inside the wall's box, so it always passes.
function crossesBox(flat,o,ax,ay,sdx,sdy,margin){
 let t0=0,t1=1;
 if(sdx===0){if(ax<flat[o]-margin||ax>flat[o+2]+margin)return false;}
 else{let ta=(flat[o]-margin-ax)/sdx,tb=(flat[o+2]+margin-ax)/sdx;if(ta>tb){const t=ta;ta=tb;tb=t;}if(ta>t0)t0=ta;if(tb<t1)t1=tb;if(t0>t1)return false;}
 if(sdy===0)return !(ay<flat[o+1]-margin||ay>flat[o+3]+margin);
 let ta=(flat[o+1]-margin-ay)/sdy,tb=(flat[o+3]+margin-ay)/sdy;if(ta>tb){const t=ta;ta=tb;tb=t;}if(ta>t0)t0=ta;if(tb<t1)t1=tb;return t0<=t1;
}
/** Bounded transient index. Unsupported scenes/queries retain full exact scans.
 * Optional mode 'scan' is a reference path for equivalence and benchmarking.
 * Scope membership preserves wallsNear(), including its existing semantics. */
export function createObstructionIndex(walls,{mode='indexed'}={}){
 const L=OBSTRUCTION_INDEX_LIMITS,original=walls,stats={queries:0,candidateWalls:0,fallbackQueries:0,wallCount:Array.isArray(walls)?walls.length:0,indexEntries:0,bucketCount:0,largeWalls:0,scopeBytes:0,rayScopes:0,rayEntries:0,disabledReason:''};
 let cells=new Map(),boxes=[],large=[],ordinals=new Map(),enabled=true,flat=null;const budget={vertices:0};
 const disable=reason=>{enabled=false;stats.disabledReason=reason;cells.clear();boxes=[];large=[];ordinals.clear();flat=null;stats.indexEntries=stats.bucketCount=stats.largeWalls=0;};
 if(mode==='scan')disable('reference-scan');
 else if(!Array.isArray(walls)||walls.length>L.walls)disable('scene-limit');
 else if(walls.length<16)disable('small-scene');
 else{
  for(let i=0;i<walls.length;i++){
   const w=walls[i],b=bounds(w,budget);if(!b){disable('unsupported-bounds');break;}boxes.push(b);
   // Preserve duplicate references as separate original array entries too.
   const ids=ordinals.get(w)||[];ids.push(i);ordinals.set(w,ids);
   const x0=Math.floor(b[0]/L.cellSize),y0=Math.floor(b[1]/L.cellSize),x1=Math.floor(b[2]/L.cellSize),y1=Math.floor(b[3]/L.cellSize),n=(x1-x0+1)*(y1-y0+1);
   if(n>L.wallBuckets){large.push(i);continue;}
   if(stats.indexEntries+n>L.entries){disable('entry-limit');break;}
   let exceeded=false;
   for(let x=x0;x<=x1&&!exceeded;x++)for(let y=y0;y<=y1;y++){
    const key=cellKey(x,y);let bucket=cells.get(key);
    if(!bucket){if(cells.size>=L.buckets){exceeded=true;break;}bucket=[];cells.set(key,bucket);}bucket.push(i);stats.indexEntries++;
   }
   if(exceeded){disable('bucket-limit');break;}
  }
  if(enabled){stats.bucketCount=cells.size;stats.largeWalls=large.length;
   // Flat bounds avoid per-candidate array dereferences in the query loop.
   flat=new Float64Array(boxes.length*6);boxes.forEach((b,i)=>flat.set(b,i*6));}
 }
 const seen=enabled?new Uint32Array(walls.length):null;let ticket=0;
 function scope(list,mask,forceScan=false){
  const scan=(test,fullScan)=>{stats.fallbackQueries++;stats.candidateWalls+=list.length;return test?(fullScan?fullScan():list.some(test)):list;};
  const query=(ax,ay,az,bx,by,bz,test,fullScan)=>{
   stats.queries++;
   if(!enabled||forceScan||!(finite(ax)&&finite(ay)&&finite(az)&&finite(bx)&&finite(by)&&finite(bz)))return scan(test,fullScan);
   const q0=Math.min(ax,bx)-epsilon,q1=Math.min(ay,by)-epsilon,q2=Math.max(ax,bx)+epsilon,q3=Math.max(ay,by)+epsilon,q4=Math.min(az,bz)-epsilon,q5=Math.max(az,bz)+epsilon;
   const x0=Math.floor(q0/L.cellSize),y0=Math.floor(q1/L.cellSize),x1=Math.floor(q2/L.cellSize),y1=Math.floor(q3/L.cellSize);
   if((x1-x0+1)*(y1-y0+1)>L.queryBuckets)return scan(test,fullScan);
   if(++ticket>=4294967295){seen.fill(0);ticket=1;}
   const indices=test?null:[];
   // Segment mode (existential tests) also rejects boxes the segment misses in
   // the plane; an exact hit lies inside its box, so it always passes this.
   const sweep=!!test&&x1>x0&&y1>y0,sdx=bx-ax,sdy=by-ay,margin=1e-6+4e-15*(Math.abs(ax)+Math.abs(ay)+Math.abs(bx)+Math.abs(by));
   const crosses=o=>crossesBox(flat,o,ax,ay,sdx,sdy,margin);
   // Box queries keep their candidates and order: large walls, then cells x-major,
   // each wall once, mask and box-overlap filtered. Segment mode visits a subset
   // that still contains every exact hit; an existential test may stop early.
   const visit=i=>{
    if(seen[i]===ticket)return false;seen[i]=ticket;
    if(mask&&!mask[i])return false;
    const o=i*6;if(!(flat[o]<=q2&&flat[o+2]>=q0&&flat[o+1]<=q3&&flat[o+3]>=q1&&flat[o+4]<=q5&&flat[o+5]>=q4))return false;
    if(sweep&&!crosses(o))return false;
    if(test){stats.candidateWalls++;return test(original[i]);}
    indices.push(i);return false;
   };
   for(let k=0;k<large.length;k++)if(visit(large[k]))return true;
   if(sweep){
    // Existential ray tests only need cells the segment actually crosses: an
    // exact hit lies on the segment inside the wall's box, so in one of the
    // wall's cells. Sweep each column's y-extent with a margin far larger than
    // the rounding error, instead of every cell of a long diagonal's bounding box.
    const C=L.cellSize,dx=sdx,dy=sdy,xMin=Math.min(ax,bx),xMax=Math.max(ax,bx);
    for(let x=x0;x<=x1;x++){
     const xa=Math.max(x*C,xMin),xb=Math.min((x+1)*C,xMax),ya=dx===0?ay:ay+(xa-ax)/dx*dy,yb=dx===0?by:ay+(xb-ax)/dx*dy;
     const ys=Math.max(y0,Math.floor((Math.min(ya,yb)-margin)/C)),ye=Math.min(y1,Math.floor((Math.max(ya,yb)+margin)/C)),base=(x+KEY_OFFSET)*KEY_SPAN+KEY_OFFSET;
     for(let y=ys;y<=ye;y++){const bucket=cells.get(base+y);if(bucket)for(let k=0;k<bucket.length;k++)if(visit(bucket[k]))return true;}
    }
    return false;
   }
   for(let x=x0;x<=x1;x++){const base=(x+KEY_OFFSET)*KEY_SPAN+KEY_OFFSET;for(let y=y0;y<=y1;y++){const bucket=cells.get(base+y);if(bucket)for(let k=0;k<bucket.length;k++)if(visit(bucket[k]))return true;}}
   if(test)return false;
   indices.sort((a,b)=>a-b);const result=indices.map(i=>original[i]);stats.candidateWalls+=result.length;return result;
  };
  return Object.freeze({queryPoint:(x,y,z)=>query(x,y,z,x,y,z),querySegment:(...args)=>query(...args),
   // Existential, side-effect-free predicates may stop in spatial order. The
   // caller can retain its original bulk exact scan on unsupported queries.
   anySegment:(ax,ay,az,bx,by,bz,test,fullScan)=>query(ax,ay,az,bx,by,bz,test,fullScan)});
 }
 const all=scope(original);
 return Object.freeze({...all,
  forWalls(list){return members(list).scope;},
  forRays(list,ox,oy,oz){
   const {scope:base,indices}=members(list);
   if(!indices||!finite(ox)||!finite(oy)||!finite(oz))return base;
   const rays=rayScope(indices,ox,oy,oz);
   if(!rays)return base;
   return Object.freeze({...base,anySegment:(ax,ay,az,bx,by,bz,test,fullScan)=>{
    const hit=ax===ox&&ay===oy&&test?rays(ax,ay,az,bx,by,bz,test):null;
    return hit===null?base.anySegment(ax,ay,az,bx,by,bz,test,fullScan):hit;
   }});
  },
  diagnostics:()=>({...stats})
 });
 function members(list){
  if(!enabled)return {scope:scope(list,null,true)};
  if(!Array.isArray(list)||stats.scopeBytes+walls.length>L.scopeBytes)return {scope:scope(list,null,true)};
  // Require the original order. Unexpected/reordered subsets safely use their
  // own unchanged array rather than changing the narrow-phase traversal.
  const mask=new Uint8Array(walls.length),indices=[];let previous=-1;
  for(const w of list){const choices=ordinals.get(w),i=choices?.find(i=>i>previous);if(i===undefined)return {scope:scope(list,null,true)};mask[i]=1;indices.push(i);previous=i;}
  stats.scopeBytes+=mask.byteLength;return {scope:scope(list,mask),indices};
 }
 // Rays from one origin (a camera to its samples): walls are bucketed by the
 // angle their padded box subtends from the origin, so an existential ray test
 // visits only walls in its direction, nearest first. An exact hit P lies inside
 // a wall's box and on the ray, so the ray's angle is P's angle, inside the box's
 // angular span, and P is no farther than the ray's end. Boxes are padded by more
 // than the crossing margin before their corner angles are taken; boxes near the
 // origin, where angles are ill-conditioned, are visited by every ray. Walls
 // entirely above or below the origin are kept apart and skipped exactly when
 // the height check would reject them. Far endpoints and short rays use the
 // scoped query.
 function rayScope(indices,ox,oy,oz){
  const S=L.raySectors,W=2*Math.PI/S,near=1e-3;let reach=Math.abs(ox)+Math.abs(oy);
  for(const i of indices){const o=i*6;reach=Math.max(reach,Math.abs(flat[o])+Math.abs(flat[o+1]),Math.abs(flat[o+2])+Math.abs(flat[o+3]));}
  // Rays are accepted only up to limit, so the padding exceeds every query's crossing margin.
  const limit=2*reach+1000,pad=2e-6+16e-15*limit,top=oz+epsilon,bottom=oz-epsilon;
  const groups=[[],[],[]];
  for(const i of indices){const o=i*6;groups[flat[o+4]>top?1:flat[o+5]<bottom?2:0].push(i);}
  let total=0;
  const tables=groups.map(group=>{
   const always=[],items=[];
   for(const i of group){
    const o=i*6,x0=flat[o]-pad,y0=flat[o+1]-pad,x1=flat[o+2]+pad,y1=flat[o+3]+pad;
    const distance=Math.hypot(Math.max(x0-ox,0,ox-x1),Math.max(y0-oy,0,oy-y1));
    if(distance<near){always.push(i);continue;}
    // A box left of the origin across its horizontal line wraps past ±π.
    const wraps=x1<ox&&y0<=oy&&y1>=oy;let lo=Infinity,hi=-Infinity;
    for(const [x,y] of [[x0,y0],[x1,y0],[x0,y1],[x1,y1]]){let a=Math.atan2(y-oy,x-ox);if(wraps&&a<0)a+=2*Math.PI;lo=Math.min(lo,a);hi=Math.max(hi,a);}
    const k0=Math.floor((lo-1e-9+Math.PI)/W),k1=Math.min(k0+S-1,Math.floor((hi+1e-9+Math.PI)/W));
    items.push({i,distance,k0,k1});total+=k1-k0+1;
   }
   items.sort((a,b)=>a.distance-b.distance||a.i-b.i);
   return {always,items};
  });
  if(stats.rayEntries+total>L.rayEntries)return null;stats.rayEntries+=total;stats.rayScopes++;
  for(const table of tables){
   const start=new Uint32Array(S+1),count=table.items.reduce((sum,item)=>sum+item.k1-item.k0+1,0);
   for(const {k0,k1} of table.items)for(let k=k0;k<=k1;k++)start[((k%S)+S)%S+1]++;
   for(let k=0;k<S;k++)start[k+1]+=start[k];
   const fill=start.slice(0,S),entries=new Uint32Array(count),distances=new Float64Array(count);
   for(const {i,distance,k0,k1} of table.items)for(let k=k0;k<=k1;k++){const e=fill[((k%S)+S)%S]++;entries[e]=i;distances[e]=distance;}
   Object.assign(table,{start,entries,distances});delete table.items;
  }
  return (ax,ay,az,bx,by,bz,test)=>{
   const sdx=bx-ax,sdy=by-ay,length=Math.hypot(sdx,sdy);
   if(!(finite(az)&&finite(bx)&&finite(by)&&finite(bz))||az!==oz||Math.abs(bx)+Math.abs(by)>limit||length<near)return null;
   stats.queries++;
   if(++ticket>=4294967295){seen.fill(0);ticket=1;}
   const q0=Math.min(ax,bx)-epsilon,q1=Math.min(ay,by)-epsilon,q2=Math.max(ax,bx)+epsilon,q3=Math.max(ay,by)+epsilon,q4=Math.min(az,bz)-epsilon,q5=Math.max(az,bz)+epsilon;
   const margin=1e-6+4e-15*(Math.abs(ax)+Math.abs(ay)+Math.abs(bx)+Math.abs(by)),reachable=length*(1+1e-9)+pad;
   const visit=i=>{
    if(seen[i]===ticket)return false;seen[i]=ticket;
    const o=i*6;if(!(flat[o]<=q2&&flat[o+2]>=q0&&flat[o+1]<=q3&&flat[o+3]>=q1&&flat[o+4]<=q5&&flat[o+5]>=q4))return false;
    if(!crossesBox(flat,o,ax,ay,sdx,sdy,margin))return false;
    stats.candidateWalls++;return test(original[i]);
   };
   const angle=Math.atan2(sdy,sdx),k0=Math.floor((angle-1e-12+Math.PI)/W),k1=Math.floor((angle+1e-12+Math.PI)/W);
   // Above-origin walls fail the height check unless the ray rises; below-origin ones unless it falls.
   for(let g=0;g<3;g++){
    if(g===1&&!(bz>az)||g===2&&!(bz<az))continue;
    const {always,start,entries,distances}=tables[g];
    for(let k=0;k<always.length;k++)if(visit(always[k]))return true;
    for(let k=k0;k<=k1;k++){const sector=((k%S)+S)%S;for(let e=start[sector],end=start[sector+1];e<end&&distances[e]<=reachable;e++)if(visit(entries[e]))return true;}
   }
   return false;
  };
 }
}
